import { GroundTruthJob, GroundTruthJobStatus } from "@app/shared";

/**
 * Formatting for a ground truth result, shared by the jobs table and the completion toast so the
 * two can never word the same outcome differently.
 */

export type ClassCounts = Record<string, number>;

export type GroundTruthDelta = {
    total: number;
    byClass: ClassCounts;
    comparedTo: GroundTruthJob;
};

export function parseClassCounts(json: string | undefined): ClassCounts {
    if (!json) return {};
    try {
        const parsed = JSON.parse(json);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

/** "Semitruck_Tractor" -> "semi truck tractor"; the model's class names aren't written for people. */
export function classLabel(className: string): string {
    return className.replace(/_/g, " ").toLowerCase();
}

function pluralize(count: number, className: string): string {
    return `${count} ${classLabel(className)}${Math.abs(count) === 1 ? "" : "s"}`;
}

/** "9 cars, 3 semi truck tractors, 1 trailer" — biggest group first. */
export function describeClassCounts(counts: ClassCounts): string {
    return Object.entries(counts)
        .sort(([aName, aCount], [bName, bCount]) => bCount - aCount || aName.localeCompare(bName))
        .map(([className, count]) => pluralize(count, className))
        .join(", ");
}

/**
 * The completed job covering the moment most recently before `job`'s, counting the *same* target
 * types. A count is only comparable to another count that counted the same things, so a run with a
 * different filter is skipped rather than differenced against.
 *
 * Ordered by asOfDate — the moment the photos represent — and NOT by the order the API returns,
 * which is by requestDate. Those come apart exactly when this feature expects them to: a batch can
 * be uploaded hours or days after it was taken, and every other part of the feature is careful to
 * reason in asOfDate for that reason. Keyed on upload order, backfilling last Tuesday's photos today
 * would make them "the previous set" for every run since, and the deltas either side would describe
 * time running backwards.
 *
 * Ties on asOfDate fall back to requestDate, so two sets stamped the same minute still order
 * predictably rather than by whatever the list happened to hand us.
 */
export function findComparablePrevious(job: GroundTruthJob, jobs: GroundTruthJob[]): GroundTruthJob | undefined {
    const moment = (j: GroundTruthJob) => new Date(j.asOfDate ?? 0).getTime();
    const requested = (j: GroundTruthJob) => new Date(j.requestDate ?? 0).getTime();
    const isBefore = (candidate: GroundTruthJob) =>
        moment(candidate) < moment(job)
        || (moment(candidate) === moment(job) && requested(candidate) < requested(job));

    return jobs
        .filter(candidate =>
            candidate.id !== job.id
            && candidate.status === GroundTruthJobStatus.Complete
            && candidate.totalCount != null
            && candidate.targetFilter === job.targetFilter
            && isBefore(candidate))
        .sort((a, b) => moment(b) - moment(a) || requested(b) - requested(a))[0];
}

/** Per-class change against the previous comparable run, or undefined when there isn't one. */
export function computeDelta(job: GroundTruthJob, jobs: GroundTruthJob[]): GroundTruthDelta | undefined {
    const previous = findComparablePrevious(job, jobs);
    if (!previous) return undefined;

    const current = parseClassCounts(job.classCountsJson);
    const before = parseClassCounts(previous.classCountsJson);
    const byClass: ClassCounts = {};
    Array.from(new Set([...Object.keys(current), ...Object.keys(before)])).forEach(className => {
        const change = (current[className] ?? 0) - (before[className] ?? 0);
        if (change !== 0) byClass[className] = change;
    });

    return { total: (job.totalCount ?? 0) - (previous.totalCount ?? 0), byClass, comparedTo: previous };
}

function signed(value: number): string {
    if (value > 0) return `+${value}`;
    if (value < 0) return `−${Math.abs(value)}`;
    return "±0";
}

/** "+3 vs the previous set (+2 cars, +1 semi truck tractor)". */
export function describeDelta(delta: GroundTruthDelta): string {
    if (delta.total === 0 && Object.keys(delta.byClass).length === 0) {
        return "no change from the previous set";
    }
    const parts = Object.entries(delta.byClass)
        .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
        .map(([className, change]) => `${signed(change)} ${classLabel(className)}${Math.abs(change) === 1 ? "" : "s"}`);
    const headline = `${signed(delta.total)} vs the previous set`;
    return parts.length ? `${headline} (${parts.join(", ")})` : headline;
}

/** The one-line result for a completed job: total, breakdown, and change if there's something to compare to. */
export function describeResult(job: GroundTruthJob, jobs: GroundTruthJob[]): string {
    const breakdown = describeClassCounts(parseClassCounts(job.classCountsJson));
    const total = job.totalCount ?? 0;
    const headline = `${total} ${total === 1 ? "vehicle" : "vehicles"}${breakdown ? ` — ${breakdown}` : ""}`;
    const delta = computeDelta(job, jobs);
    return delta ? `${headline}. ${describeDelta(delta)}.` : headline;
}
